import axios from "axios";
import { isHubSocialHost, normalizeHostname, socialPageOrigin, } from "../config/agencyRuntime";
import { resolveUserApiV1Base } from "../api/resolveBaseUrl";
const NOTICE_STORAGE_KEY = "chat_launch_notice";
/** null = chưa load config; false = BE tắt sunset; true = bật */
let hubSunsetFeatureEnabled = null;
export function resetHubSunsetFeatureCache() {
    hubSunsetFeatureEnabled = null;
}
function legacyLaunchHostsFromEnv() {
    const raw = process.env.VUE_APP_LEGACY_LAUNCH_SOCIAL_HOSTS
        || process.env.VUE_APP_AGENCY_HUB_SOCIAL_HOSTS
        || "social.chatteam.asia";
    return String(raw)
        .split(",")
        .map((h) => normalizeHostname(h))
        .filter(Boolean);
}
/** Trang đang mở trên host legacy (social.chatteam.asia), không phải social.{apex} agency. */
export function isLegacyLaunchPageHost() {
    if (typeof window === "undefined") {
        return false;
    }
    if (isHubSocialHost()) {
        return true;
    }
    const host = normalizeHostname(window.location.hostname);
    return legacyLaunchHostsFromEnv().includes(host);
}
export function syncHubSunsetFromPlatform(platform) {
    // Chỉ bật sunset khi BE bật + host legacy (SSO platform) + URL trình duyệt là legacy launch host.
    // Tránh GET /auth/hub-sunset → 404 trên social.gmv.asia (API chatapi.gmv.asia).
    const enabled = !!(platform?.legacy_launch_notice_enabled
        && platform.is_legacy_social_host === true
        && isLegacyLaunchPageHost());
    hubSunsetFeatureEnabled = enabled;
    if (!enabled) {
        clearHubSunsetStorage();
    }
}
export function isHubSunsetFeatureEnabled() {
    return hubSunsetFeatureEnabled === true;
}
export function clearHubSunsetStorage() {
    try {
        sessionStorage.removeItem(NOTICE_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
}
export function readStoredChatLaunchNotice() {
    if (hubSunsetFeatureEnabled === false) {
        return null;
    }
    try {
        const raw = sessionStorage.getItem(NOTICE_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return parsed && parsed.show ? parsed : null;
    }
    catch {
        return null;
    }
}
export function hasPendingHubSunset() {
    if (hubSunsetFeatureEnabled === false) {
        return false;
    }
    return readStoredChatLaunchNotice() !== null;
}
let hubSunsetFetchInflight = null;
/**
 * Lấy sunset notice cho user đã đăng nhập (cookie/token có sẵn) — dùng khi user vào lại
 * trang sau khi BE bật sunset; không cần logout + login lại.
 * Tự lưu vào sessionStorage để `hasPendingHubSunset()` true → router force HubSunset.
 */
export async function ensureHubSunsetNoticeFetched() {
    if (typeof window === "undefined") {
        return null;
    }
    if (!isLegacyLaunchPageHost()) {
        hubSunsetFeatureEnabled = false;
        return null;
    }
    if (hubSunsetFeatureEnabled === false) {
        return null;
    }
    const cached = readStoredChatLaunchNotice();
    if (cached) {
        return cached;
    }
    if (hubSunsetFetchInflight) {
        return hubSunsetFetchInflight;
    }
    const url = `${resolveUserApiV1Base()}/auth/hub-sunset`;
    hubSunsetFetchInflight = axios
        .get(url, { validateStatus: (s) => s < 500 })
        .then((res) => {
        // 404 → API/host không hỗ trợ hub-sunset → tắt fetch lần sau.
        if (res.status === 404) {
            hubSunsetFeatureEnabled = false;
            return null;
        }
        const notice = (res?.data?.data ?? res?.data);
        if (notice && notice.show) {
            try {
                sessionStorage.setItem(NOTICE_STORAGE_KEY, JSON.stringify(notice));
            }
            catch {
                /* ignore */
            }
            return notice;
        }
        return null;
    })
        .catch(() => null)
        .finally(() => {
        hubSunsetFetchInflight = null;
    });
    return hubSunsetFetchInflight;
}
const HUB_PM_FLOW_KEY = "hub_pm_flow";
/** Deep link /pm/{username} trên hub — cho phép mở messenger dù đang HubSunset. */
export function markHubPmFlow() {
    if (typeof window === "undefined")
        return;
    try {
        sessionStorage.setItem(HUB_PM_FLOW_KEY, "1");
    }
    catch {
        /* ignore */
    }
}
export function isHubPmFlow() {
    if (typeof window === "undefined")
        return false;
    try {
        return sessionStorage.getItem(HUB_PM_FLOW_KEY) === "1";
    }
    catch {
        return false;
    }
}
export function shouldForceHubSunsetRoute(routeName) {
    if (!hasPendingHubSunset()) {
        return false;
    }
    if (!routeName || routeName === "HubSunset" || routeName === "Login" || routeName === "AgencyLogin") {
        return false;
    }
    if (routeName === "LegacySocialRedirect" || routeName === "Update user page") {
        return false;
    }
    // Link cố định sotax.online/pm/... → mở chat 1-1 không bị chặn sunset
    if (routeName === "private_pm" ||
        ((routeName === "messenger" || routeName === "messenger_dm") && isHubPmFlow())) {
        return false;
    }
    return true;
}
export function navigateAfterLogin(sunset) {
    if (typeof window === "undefined") {
        return;
    }
    let savedPm = null;
    try {
        savedPm = sessionStorage.getItem("post_login_redirect");
        if (savedPm) {
            sessionStorage.removeItem("post_login_redirect");
        }
    }
    catch {
        savedPm = null;
    }
    // Ưu tiên quay lại deep link /pm/... sau login (hub)
    if (savedPm && savedPm.startsWith("/pm/")) {
        markHubPmFlow();
        const target = `${socialPageOrigin()}${savedPm}`;
        import("../router").then(({ default: router }) => {
            router.replace(savedPm).catch(() => {
                window.location.assign(target);
            });
        }).catch(() => {
            window.location.assign(target);
        });
        return;
    }
    const path = sunset ? "/auth/hub-sunset" : "/new-feeds";
    const target = `${socialPageOrigin()}${path}`;
    import("../router").then(({ default: router }) => {
        router
            .replace({ name: sunset ? "HubSunset" : "New Feeds" })
            .catch(() => {
            window.location.assign(target);
        });
    }).catch(() => {
        window.location.assign(target);
    });
    window.setTimeout(() => {
        const current = window.location.pathname.replace(/\/$/, "") || "/";
        const want = path.replace(/\/$/, "") || "/";
        if (current !== want && !current.startsWith(want + "/")) {
            window.location.assign(target);
        }
    }, 400);
}
